"use client";

import * as React from "react";
import { PhoneCall, ShieldCheck, Clock, Wrench, CircleCheck as CheckCircle2, TriangleAlert as AlertTriangle, ArrowRight } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
export default function DisposalHero() {
  const shouldReduceMotion = useReducedMotion();
  const trustHighlights = [{
    title: "Same-Day Fixes",
    description: "Jammed, humming, or leaking units fixed fast.",
    icon: Clock
  }, {
    title: "Upfront Estimates",
    description: "No hidden charges or surprise dispatch fees.",
    icon: ShieldCheck
  }, {
    title: "All Major Brands",
    description: "InSinkErator, Moen, Waste King, and more.",
    icon: Wrench
  }];
  const commonIssues = ["Humming noise with no blade spin", "Water leaking under sink base", "Standing water and slow drain", "Reset button tripping constantly"];
  return <section id="garbagedisposalhero" data-nav="light" className="relative overflow-hidden bg-background py-12 lg:py-20">
      {/* Background Decorative Gradients */}
      <div className="pointer-events-none absolute inset-0 -z-10 flex items-center justify-center overflow-hidden">
        <div className="absolute -top-24 left-1/2 -translate-x-1/2 h-[450px] w-[750px] rounded-full bg-secondary/50 blur-3xl opacity-60" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-8">
          
          {/* Left Column: Copy & CTAs */}
          <motion.div initial={shouldReduceMotion ? {} : {
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }} className="flex flex-col items-start lg:col-span-7">
            <div className="mb-4 inline-flex items-center gap-2">
              <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
                Jonesboro Emergency Plumber
              </Badge>
              <Badge variant="outline" className="border-accent/40 bg-accent/10 px-3 py-1 text-xs font-medium text-foreground">
                24/7 Fast Local Dispatch
              </Badge>
            </div>

            <h1 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl lg:text-5xl">
              Garbage Disposal Repair &amp; Rapid Replacement in Jonesboro
            </h1>

            <p className="mt-5 text-base leading-relaxed text-muted-foreground sm:text-lg">
              Is your kitchen disposal jammed, leaking under the cabinet, or making a loud humming sound? 
              Our licensed local plumbers arrive quickly with fully stocked trucks to unjam, repair, or replace your disposal on the spot.
            </p>

            {/* Quick Diagnostic Checklist */}
            <div className="mt-6 w-full rounded-xl border border-border bg-card/60 p-4 backdrop-blur-xs">
              <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                <AlertTriangle className="size-4 text-accent" />
                <span>Common disposal problems we solve immediately:</span>
              </div>
              <ul className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                {commonIssues.map((issue, i) => <li key={i} data-index={i} className="flex items-start gap-2 text-xs text-muted-foreground sm:text-sm">
                    <CheckCircle2 className="size-4 shrink-0 text-primary mt-0.5" />
                    <span>{issue}</span>
                  </li>)}
              </ul>
            </div>

            {/* CTAs */}
            <div className="mt-8 flex flex-col gap-3.5 sm:flex-row sm:items-center">
              <a href="tel:4708479076" className="inline-block">
                <Button size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 text-base font-semibold shadow-md gap-2">
                  <PhoneCall className="size-5 text-accent" />
                  <span>Call (470) 847-9076 Now</span>
                </Button>
              </a>

              <Link to="/contact/" className="inline-block">
                <Button size="lg" variant="outline" className="w-full sm:w-auto text-base font-medium border-border hover:bg-muted text-foreground gap-2">
                  <span>Request Online Quote</span>
                  <ArrowRight className="size-4" />
                </Button>
              </Link>
            </div>

            <p className="mt-3 text-xs text-muted-foreground">
              Speak directly with an on-duty technician. Average arrival time under 45 minutes in Jonesboro, GA.
            </p>
          </motion.div>

          {/* Right Column: Hero Visual & Fast Booking Card */}
          <motion.div initial={shouldReduceMotion ? {} : {
          opacity: 0,
          scale: 0.98
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6,
          delay: 0.1,
          ease: "easeOut"
        }} className="lg:col-span-5">
            <div className="relative mx-auto max-w-md lg:max-w-none">
              <div className="relative overflow-hidden rounded-2xl border border-border bg-card shadow-xl">
                <div className="relative h-64 sm:h-72 w-full overflow-hidden bg-muted">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/garbage_disposal_repair_hero.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/garbage_disposal_repair_hero.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/garbage_disposal_repair_hero.png-wvc-sizes" alt="Plumber inspecting and repairing a garbage disposal under kitchen sink" loading="lazy" decoding="async" className="h-full w-full object-cover object-center" />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent" />
                  
                  <div className="absolute bottom-3 left-3 right-3 rounded-lg bg-background/95 p-3 backdrop-blur-xs border border-border/80 shadow-xs">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="relative flex size-3">
                          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent opacity-75" />
                          <span className="relative inline-flex size-3 rounded-full bg-accent" />
                        </span>
                        <span className="text-xs font-semibold text-foreground">Plumber On Call in Jonesboro</span>
                      </div>
                      <span className="text-xs font-medium text-muted-foreground">Available 24/7</span>
                    </div>
                  </div>
                </div>

                <div className="p-5 sm:p-6">
                  <h2 className="text-base font-semibold text-foreground">Why Neighbors Trust Our Disposal Service</h2>
                  <div className="mt-4 space-y-3">
                    {trustHighlights.map((item, index) => {
                    const IconComponent = item.icon;
                    return <div key={index} data-index={index} className="flex items-start gap-3 rounded-lg bg-muted/50 p-3 transition-[background-color,border-color] hover:bg-muted">
                          <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground">
                            <IconComponent className="size-4" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-foreground">{item.title}</p>
                            <p className="text-xs text-muted-foreground">{item.description}</p>
                          </div>
                        </div>;
                  })}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

        </div>

        {/* Bottom Feature Pill Row */}
        <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-3 border-t border-border pt-8">
          <Card className="border-border/60 bg-card/40 py-4 shadow-none">
            <CardContent className="flex items-center gap-3 px-4 py-0">
              <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <Wrench className="size-5" />
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground">Unjamming &amp; Motor Repairs</p>
                <p className="text-xs text-muted-foreground">Blade clearing, wiring, &amp; seal replacements</p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-border/60 bg-card/40 py-4 shadow-none">
            <CardContent className="flex items-center gap-3 px-4 py-0">
              <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <ShieldCheck className="size-5" />
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground">New Unit Installations</p>
                <p className="text-xs text-muted-foreground">High-horsepower &amp; quiet series available</p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-border/60 bg-card/40 py-4 shadow-none">
            <CardContent className="flex items-center gap-3 px-4 py-0">
              <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <Clock className="size-5" />
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground">Emergency Night &amp; Weekend Service</p>
                <p className="text-xs text-muted-foreground">No extra fees for local emergency calls</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>;
}